import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenu as UI_DropdownMenu,
} from "components/ui/dropdown-menu";
import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Ban, Edit, MoreHorizontal } from "lucide-react";

import { apiSlice } from "@/services/http";
import { Tags } from "@/enum/tags";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useCancelRegionalSubstitutionMutation } from "@/services/regional-substitution";
import { IRegionalSubstitution } from "@/@types/regional-substitution";
import {
  CANCELABLE_STATUSES,
  EDITABLE_STATUSES,
} from "../../constants/status";
import { REGIONAL_SUBSTITUTION_MESSAGES } from "../../constants/messages";
import { REGIONAL_SUBSTITUTION_ROUTES } from "../../constants/routes";
import { isConflictError } from "@/utils/error-mapper";
import { CancelConfirmModal } from "./cancel-confirm-modal";

function CancelModalContainer({
  row,
  onClose,
}: {
  row: IRegionalSubstitution;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const [cancelRegionalSubstitution, { isLoading }] =
    useCancelRegionalSubstitutionMutation();

  const handleConfirm = async () => {
    if (isLoading) return;
    try {
      await cancelRegionalSubstitution({
        id: row.id,
        version: row.version,
      }).unwrap();
      onClose();
      toast(REGIONAL_SUBSTITUTION_MESSAGES.CANCEL.SUCCESS);
    } catch (error) {
      onClose();
      if (isConflictError(error)) {
        dispatch(apiSlice.util.invalidateTags([Tags.REGIONAL_SUBSTITUTION]));
        toast(REGIONAL_SUBSTITUTION_MESSAGES.CANCEL.CONFLICT);
        return;
      }
      toast(REGIONAL_SUBSTITUTION_MESSAGES.CANCEL.ERROR);
    }
  };

  return (
    <CancelConfirmModal
      regionalName={row.regional.name}
      gerenteName={row.user.name}
      onConfirm={handleConfirm}
      onCancel={onClose}
      isLoading={isLoading}
    />
  );
}

function DropdownMenuComponent({ row }: { row: IRegionalSubstitution }) {
  const { handleModal } = useDialogModal();
  const navigate = useNavigate();

  const canEdit = EDITABLE_STATUSES.includes(row.status);
  const canCancel = CANCELABLE_STATUSES.includes(row.status);

  const handleEdit = () => {
    navigate(REGIONAL_SUBSTITUTION_ROUTES.editById(row.id));
  };

  const openCancelModal = () => {
    handleModal({
      isOpen: true,
      title: "Cancelar substituição",
      element: (
        <CancelModalContainer
          row={row}
          onClose={() => handleModal({ isOpen: false })}
        />
      ),
    });
  };

  return (
    <UI_DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 bg-transparent p-0 hover:bg-gray-100"
        >
          <span className="sr-only">Abrir menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canEdit && (
          <DropdownMenuItem className="cursor-pointer" onClick={handleEdit}>
            <Edit className="mr-2 h-4 w-4" />
            Editar vigência
          </DropdownMenuItem>
        )}
        {canCancel && (
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={openCancelModal}
          >
            <Ban className="mr-2 h-4 w-4" />
            Cancelar vínculo
          </DropdownMenuItem>
        )}
        {!canEdit && !canCancel && (
          <DropdownMenuItem disabled>
            <span className="text-gray-400">Vínculo encerrado</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </UI_DropdownMenu>
  );
}

export default function ActionTable({ row }: { row: IRegionalSubstitution }) {
  return <DropdownMenuComponent row={row} />;
}
